import { from } from 'rxjs';
import { concatMap, defaultIfEmpty, filter, last, map, mergeMap, scan, shareReplay, } from 'rxjs/operators';
import { BaseConfigBuilder, } from '@equinor/fusion-framework-module';
import { toObservable } from '@equinor/fusion-observable';
import { mergeMetadata } from './utils/merge-telemetry-item.js';
/**
 * Configures telemetry settings for the application.
 *
 * The `TelemetryConfigurator` class extends `BaseConfigBuilder` to provide a fluent API for
 * setting up telemetry adapters, metadata, default scopes, and parent providers.
 *
 * @example
 * ```typescript
 * const configurator = new TelemetryConfigurator()
 *   .setAdapter(myAdapter)
 *   .setMetadata({ app: 'my-app' })
 *   .setDefaultScope(['user', 'session'])
 *   .setParent(parentProvider);
 * ```
 *
 * @remarks
 * - Adapters are managed internally and can be set using `setAdapter`.
 * - Metadata, default scope, and parent provider can be configured via their respective methods.
 * - Filters should be applied directly to individual adapters when they are created.
 * - All setter methods return `this` for method chaining.
 *
 * @see BaseConfigBuilder
 * @see ITelemetryConfigurator
 */
export class TelemetryConfigurator extends BaseConfigBuilder {
    #adaptersCallbacks = {};
    #metadata = [];
    /**
     * Creates a new `TelemetryConfigurator` and wires up the async adapter
     * resolution and metadata merging pipelines used by `createConfigAsync`.
     */
    constructor() {
        super();
        // Configure async adapter resolution using mergeMap to handle Promise/Observable adapter factories
        this._set('adapters', (args) => {
            // Resolve every adapter factory concurrently and merge them into a single record
            return from(Object.entries(this.#adaptersCallbacks)).pipe(mergeMap(([identifier, adapterFn]) => 
            // Drop adapters whose factory resolved to a falsy value
            from(adapterFn(args)).pipe(filter((adapter) => !!adapter), map((adapter) => [identifier, adapter]))), scan((acc, [identifier, adapter]) => {
                acc[identifier] = adapter;
                return acc;
            }, {}), defaultIfEmpty({}), shareReplay({ bufferSize: 1, refCount: true }));
        });
        // Configure metadata merging - handles multiple sync/async metadata sources
        this._set('metadata', async () => {
            const metadataItems = this.#metadata;
            return (...args) => 
            // Merge every registered metadata source in order, emitting the final accumulated result
            from(metadataItems).pipe(concatMap((metadata) => toObservable(metadata, ...args)), scan((acc, current) => mergeMetadata(acc, current) ?? {}, {}), last(), shareReplay({ bufferSize: 1, refCount: true }));
        });
    }
    /**
     * Registers a telemetry adapter with the configurator.
     *
     * @param identifier - The key used to register and later resolve this adapter.
     * @param adapter - The telemetry adapter to be added. The adapter's identifier is used as the key.
     * @returns The current instance of the configurator for method chaining.
     */
    setAdapter(identifier, adapter) {
        return this.configureAdapter(identifier, async () => adapter);
    }
    /**
     * Configures a telemetry adapter with the configurator.
     *
     * @param identifier - The key used to register and later resolve this adapter.
     * @param adapterFn - A callback function that returns a telemetry adapter instance
     * @returns The current instance for method chaining
     */
    configureAdapter(identifier, adapterFn) {
        this.#adaptersCallbacks[identifier] = adapterFn;
        return this;
    }
    /**
     * Sets the metadata configuration for telemetry.
     *
     * @remarks
     * All setting will accumulate, and the metadata will be merged.
     *
     * @param metadata - The metadata object or a callback function that receives and returns the metadata configuration.
     * @returns The current instance for method chaining.
     */
    setMetadata(metadata) {
        this.#metadata.push(metadata);
        return this;
    }
    /**
     * Sets the default scope for telemetry operations.
     *
     * @param scope - An array of strings representing the default scope to be used.
     * @returns The current instance for method chaining.
     */
    setDefaultScope(scope) {
        this._set('defaultScope', scope);
        return this;
    }
    /**
     * Sets the parent telemetry provider for this configurator.
     *
     * @param parent - The parent telemetry provider to associate, or `undefined` to remove the parent.
     * @returns The current instance for method chaining.
     */
    setParent(parent) {
        this._set('parent', parent);
        return this;
    }
    /**
     * Attaches an observable stream of telemetry items to the configurator.
     *
     * @param item$ - An observable input stream of telemetry items to be processed.
     * @returns The current instance for method chaining.
     */
    attachItems(item$) {
        this._set('items$', item$);
        return this;
    }
    /**
     * Sets the filter configuration for telemetry items.
     *
     * The filter determines which telemetry items should be passed to adapters and/or relayed to parent providers.
     *
     * @param filter - Either a filter object with `adapter` and `relay` functions, or a callback that returns such a filter object.
     * @returns The current instance for method chaining.
     */
    setFilter(filter) {
        this._set('filter', filter);
        return this;
    }
}
//# sourceMappingURL=TelemetryConfigurator.js.map